import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// MANAGERS
import { SOUND_SETTINGS_DEFAULTS } from '@GameStore/Defaults';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameStoreEvents } from '@Managers/Events/GameStoreEvents';
import {
    getAuthenticateGameConfig,
    getFeatures,
    getGlobalStyles,
    getLeftHandMode,
    getPreloaderState,
    getSoundSettings,
    getWCAMode,
    setLeftHandMode,
    setWCAMode
} from '@Managers/Redux/AppState/AppState';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';

// MODULES
import { Checkbox } from '@UILayer/Inputs/Checkbox';

// STYLES
import styles from '@UILayer/UILayer.scss';

// TYPES
import { ESoundSettingsKeys } from '@GameStore/GameStoreSchema';

// IMAGES
import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { IPreloaderState } from '@Core/PreloaderScene/PreloaderScene.types';
import HistoryCheck from './HistoryCheck/HistoryCheck';
import { ISettingsState } from './Settings.types';

import { DOM_ID } from '@UILayer/UILayer.types';

const Settings: React.FC<{}> = () => {
    const [state, setState] = useReducer((state: ISettingsState, newState: ISettingsState) => ({ ...state, ...newState }), {
        soundSettings: SOUND_SETTINGS_DEFAULTS,
        preloaderState: {},
        disabledGambler: false,
        backgroundMusic: true
    });

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const leftHandMode = useAppSelector(getLeftHandMode);
    const gameStoreSoundSettings = useAppSelector(getSoundSettings);
    const gameStorePreloaderState = useAppSelector(getPreloaderState);
    const gameStoreAuthenticateGameConfig = useAppSelector(getAuthenticateGameConfig);
    const gameStoreFeatures = useAppSelector(getFeatures);

    const globalStyles = useAppSelector(getGlobalStyles);
    const containerCN = cn(
        styles.settings_content_settings,
        globalStyles.orientation,
        globalStyles.handMode,
        globalStyles.device,
        globalStyles.wideDevice,
        globalStyles.screenSize,
        globalStyles.isTablet
    );
    const wcaMode = useAppSelector(getWCAMode);

    const updateSound = (val: ESoundSettingsKeys) => {
        const newSoundState = { ...state.soundSettings, [val]: !state.soundSettings[val] };
        EventsCenter.emit(EGameStoreEvents.GAME_STORE_SOUND_STATE_UPDATE, newSoundState);
    };

    const toggleLeftHand = () => {
        dispatch(setLeftHandMode(!leftHandMode));
    };

    function toggleWCAMode() {
        dispatch(setWCAMode(!wcaMode));
    }

    function togglePreloaderScene(data: IPreloaderState) {
        EventsCenter.emit(EGameStoreEvents.GAME_STORE_PRELOADER_STATE_UPDATE, data);
    }

    useEffect(() => {
        setState({
            soundSettings: gameStoreSoundSettings,
            preloaderState: gameStorePreloaderState,
            disabledGambler: gameStoreAuthenticateGameConfig.gamble.maxTries === 0 ? true : false,
            backgroundMusic: !!gameStoreFeatures.backgroundMusic
        });
    }, [gameStoreSoundSettings, gameStorePreloaderState, gameStoreAuthenticateGameConfig, gameStoreFeatures]);

    return (
        <div className={containerCN}>
            <div className={styles.content_col}>
                <h2 className={styles.col_subheader}>{t('SOUND_SETTINGS')}</h2>
                <div className={styles.col_items_group}>
                    <div className={cn(styles.settings_item, state.soundSettings.muteAll ? styles.settings_disabled : '')}>
                        <Checkbox
                            label={t('REEL_STOP')}
                            id={DOM_ID.SETTINGS_SOUND_REEL_STOP_CHECKBOX}
                            checked={state.soundSettings.reelStop}
                            onChange={() => updateSound(ESoundSettingsKeys.reelStop)}
                        />
                    </div>

                    {!state.disabledGambler && (
                        <div className={cn(styles.settings_item, state.soundSettings.muteAll ? styles.settings_disabled : '')}>
                            <Checkbox
                                label={t('GAMBLE_SHUFFLE')}
                                id={DOM_ID.SETTINGS_SOUND_GAMBLE_SHUFFLE_STOP_CHECKBOX}
                                checked={state.soundSettings.gambleShuffle}
                                onChange={() => updateSound(ESoundSettingsKeys.gambleShuffle)}
                            />
                        </div>
                    )}

                    {state.backgroundMusic && (
                        <div className={cn(styles.settings_item, state.soundSettings.muteAll ? styles.settings_disabled : '')}>
                            <Checkbox
                                label={t('BACKGROUND_MUSIC')}
                                id={DOM_ID.SETTINGS_SOUND_BACKGROUND_CHECKBOX}
                                checked={state.soundSettings.background}
                                onChange={() => updateSound(ESoundSettingsKeys.background)}
                            />
                        </div>
                    )}

                    <div className={styles.settings_item}>
                        <Checkbox
                            label={t('MUTE_ALL')}
                            id={DOM_ID.SETTINGS_SOUND_MUTEALL_CHECKBOX}
                            checked={state.soundSettings.muteAll}
                            onChange={() => updateSound(ESoundSettingsKeys.muteAll)}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.content_col}>
                <h2 className={styles.col_subheader}>{t('OTHER_SETTINGS')}</h2>
                <div className={styles.col_items_group}>
                    <div className={styles.settings_item}>
                        <Checkbox
                            label={t('LEFT_HAND_MODE')}
                            id={DOM_ID.SETTINGS_LEFT_HAND_CHECKBOX}
                            checked={leftHandMode}
                            onChange={() => toggleLeftHand()}
                        />
                    </div>

                    {state.preloaderState.introMovieAvailable && (
                        <div className={styles.settings_item}>
                            <Checkbox
                                label={t('SETTINGS_SHOW_INTRO_MOVIE_LABEL')}
                                id={DOM_ID.SETTINGS_SHOW_INTRO_CHECKBOX}
                                checked={state.preloaderState.showIntroMovie}
                                onChange={() => togglePreloaderScene({ showPreloaderInfo: !state.preloaderState.showIntroMovie })}
                            />
                        </div>
                    )}

                    <div className={styles.settings_item}>
                        <Checkbox
                            label={t('SETTINGS_DONT_SHOW_INFO_SCREEN_LABEL')}
                            id={DOM_ID.SETTINGS_SHOW_INFO_CHECKBOX}
                            checked={!state.preloaderState.showPreloaderInfo}
                            onChange={() => togglePreloaderScene({ showPreloaderInfo: !state.preloaderState.showPreloaderInfo })}
                        />
                    </div>

                    {ConfigLoader.getConfiguration(EConfigurationNames.UI_LAYER).wcaMode && (
                        <div className={styles.settings_item}>
                            <Checkbox
                                label={t('WCA_TOGGLE_WEB_CONTENT_ACCESSIBILITY_MODE')}
                                id={DOM_ID.SETTINGS_WCA_CHECKBOX}
                                checked={wcaMode}
                                ariaLabel={t('WCA_TOGGLE_WEB_CONTENT_ACCESSIBILITY_MODE')}
                                onChange={toggleWCAMode}
                            />
                        </div>
                    )}

                    <HistoryCheck />
                </div>
            </div>
        </div>
    );
};

export default Settings;
